/**
 * The Bike Register.
 *
 * One row per bike, for its whole life. Agreements and weekly remittances
 * point at these rows rather than repeating a plate, so the register is the
 * only place a registration, a model or an owner is written down.
 *
 * Changing hands is its own action, not a field on the edit form. A bike that
 * moved three times has a history, and overwriting a `riderId` would keep only
 * the last of it — which is exactly the question a damaged bike or a disputed
 * remittance raises.
 */

import { useState, type ReactNode } from 'react';
import { Bike as BikeIcon, Plus } from 'lucide-react';
import {
  BIKE_OWNERSHIPS,
  BIKE_OWNERSHIP_LABELS,
  SELECTABLE_BIKE_OWNERSHIPS,
  BIKE_STATUSES,
  BIKE_STATUS_LABELS,
  VEHICLE_CATEGORIES,
  VEHICLE_CATEGORY_LABELS,
  type BikeOwnership,
  type BikeStatus,
  type BikeView,
  type VehicleCategory,
} from '@cenox/shared';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  Select,
  TableSkeleton,
  Textarea,
} from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { ApiError } from '../../lib/api';
import { useWorkingLocation } from '../../lib/locationContext';
import { useRiders } from '../logistics/data';
import { BikeStatusBadge, InvestorName, SectionTitle, Stat } from './parts';
import {
  useAssignBike,
  useBike,
  useBikes,
  useCreateBike,
  useReleaseBike,
  useUpdateBike,
} from './data';
import { BikeOwnershipPanel } from './BikeOwnershipPanel';
import { useInvestorOptions } from './investorData';

const STATUS_OPTIONS = BIKE_STATUSES.map((s) => ({ value: s, label: BIKE_STATUS_LABELS[s] }));
/** What a person chooses from: Company, Investor or Rider. */
const OWNERSHIP_OPTIONS = SELECTABLE_BIKE_OWNERSHIPS.map((o) => ({
  value: o,
  label: BIKE_OWNERSHIP_LABELS[o],
}));
/** What the register can be filtered by, older "Other" bikes included. */
const OWNERSHIP_FILTER_OPTIONS = BIKE_OWNERSHIPS.map((o) => ({ value: o, label: BIKE_OWNERSHIP_LABELS[o] }));

/** "Investor · Ada Okafor", "Company", "Rider · Musa". */
export function ownerLabel(bike: Pick<BikeView, 'ownership' | 'investorName' | 'ownerName'>): {
  kind: string;
  name: string | null;
} {
  return {
    kind: BIKE_OWNERSHIP_LABELS[bike.ownership],
    name: bike.ownership === 'investor' ? (bike.investorName ?? bike.ownerName) : bike.ownerName,
  };
}

/** The owner's name; an investor's opens their Finance profile. */
function OwnerName({ bike, className }: { bike: Pick<BikeView, 'ownership' | 'investorId' | 'investorName' | 'ownerName'>; className: string }): ReactNode {
  const { name } = ownerLabel(bike);
  if (!name) return null;
  return bike.ownership === 'investor' ? (
    <InvestorName investorId={bike.investorId} name={name} className={className} />
  ) : (
    <span className={className}>{name}</span>
  );
}

export function BikeRegisterPage(): ReactNode {
  const { business, can } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<BikeStatus | ''>('');
  const [ownership, setOwnership] = useState<BikeOwnership | ''>('');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const register = useBikes({
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
    ...(ownership ? { ownership } : {}),
  });
  const mayManage = can('bikes:manage');
  const mayManageMoney = can('remittance:manage');

  if (register.isError) {
    return (
      <ErrorState
        message="Could not load the bike register."
        onRetry={() => void register.refetch()}
      />
    );
  }

  const totals = register.data?.totals;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label="Bikes"
          count={totals?.count ?? 0}
          hint={`${totals?.inService ?? 0} in service`}
        />
        <Stat label="On hire purchase" count={totals?.onHirePurchase ?? 0} />
        <Stat
          label="Acquisition cost"
          value={totals?.acquisitionCost ?? null}
          currency={currency}
          hint="What the fleet cost"
        />
        <Stat
          label="Estimated value"
          value={totals?.estimatedValue ?? null}
          currency={currency}
          hint="What it is worth now"
        />
      </div>

      <section>
        <SectionTitle
          aside={
            mayManage ? (
              <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                Add a bike
              </Button>
            ) : undefined
          }
        >
          Register
        </SectionTitle>

        <div className="mb-3 flex flex-wrap items-start gap-2">
          <div className="min-w-[200px] flex-1">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Registration or model"
              aria-label="Search the register"
            />
          </div>
          <div className="w-auto min-w-[160px]">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as BikeStatus | '')}
              aria-label="Filter by status"
              placeholder="All statuses"
              options={STATUS_OPTIONS}
            />
          </div>
          <div className="w-auto min-w-[160px]">
            <Select
              value={ownership}
              onChange={(e) => setOwnership(e.target.value as BikeOwnership | '')}
              aria-label="Filter by owner"
              placeholder="All owners"
              options={OWNERSHIP_FILTER_OPTIONS}
            />
          </div>
        </div>

        {register.isPending ? (
          <TableSkeleton rows={5} columns={6} />
        ) : register.data.bikes.length === 0 ? (
          <EmptyState
            icon={<BikeIcon className="size-8" />}
            title={search || status ? 'No bikes match' : 'The register is empty'}
            description="A bike is recorded once — plate, model, what it cost, who owns it — and everything else points at that row."
            action={
              mayManage ? <Button onClick={() => setCreating(true)}>Add a bike</Button> : undefined
            }
          />
        ) : (
          <Card>
            <div className="-mx-3 overflow-x-auto px-3">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3 font-medium">Registration</th>
                    <th className="py-2 pr-3 font-medium">Model</th>
                    <th className="py-2 pr-3 font-medium">Owner</th>
                    <th className="py-2 pr-3 font-medium">Rider</th>
                    <th className="py-2 pr-3 font-medium">Location</th>
                    <th className="py-2 pr-3 text-right font-medium">Value</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {register.data.bikes.map((bike) => (
                    <tr key={bike.id}>
                      <td className="py-2 pr-3 font-medium text-slate-900">{bike.registration}</td>
                      <td className="py-2 pr-3 text-slate-600">{bike.model ?? '—'}</td>
                      <td className="py-2 pr-3 text-slate-600">
                        {ownerLabel(bike).kind}
                        <OwnerName bike={bike} className="block text-xs text-slate-400" />
                      </td>
                      <td className="py-2 pr-3 text-slate-600">{bike.riderName ?? '—'}</td>
                      <td className="py-2 pr-3 text-slate-600">
                        {bike.branchName ?? <span className="text-slate-400">Unallocated</span>}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                        {bike.estimatedValue ? money(bike.estimatedValue, currency) : '—'}
                      </td>
                      <td className="py-2 pr-3">
                        <BikeStatusBadge status={bike.status} />
                      </td>
                      <td className="py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setOpenId(bike.id)}>
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {creating ? <BikeDialog onClose={() => setCreating(false)} /> : null}
      {openId ? (
        <BikeDetailDialog
          id={openId}
          currency={currency}
          mayManage={mayManage}
          mayManageMoney={mayManageMoney}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </div>
  );
}

/** Add a bike, or correct one. The same fields either way. */
function BikeDialog({ bike, onClose }: { bike?: BikeView; onClose: () => void }): ReactNode {
  const { locations } = useWorkingLocation();
  const riders = useRiders();
  const create = useCreateBike();
  const update = useUpdateBike();

  const [registration, setRegistration] = useState(bike?.registration ?? '');
  const [model, setModel] = useState(bike?.model ?? '');
  const [purchaseDate, setPurchaseDate] = useState(bike?.purchaseDate ?? '');
  const [acquisitionCost, setAcquisitionCost] = useState(bike?.acquisitionCost ?? '');
  const [estimatedValue, setEstimatedValue] = useState(bike?.estimatedValue ?? '');
  // No default: who owns a bike decides whose its remittance is, so it is asked.
  const [ownership, setOwnership] = useState<BikeOwnership | ''>(bike?.ownership ?? '');
  const [investorId, setInvestorId] = useState(bike?.investorId ?? '');
  const [ownerName, setOwnerName] = useState(bike?.ownerName ?? '');
  const investors = useInvestorOptions(!bike);
  // Blank: the business default share. Setting it is for whoever manages remittance.
  const [investorWeeklyAmount, setInvestorWeeklyAmount] = useState('');
  const mayShare = useAuth().can('remittance:manage');
  const [branchId, setBranchId] = useState(bike?.branchId ?? '');
  const [riderId, setRiderId] = useState('');
  const [category, setCategory] = useState<VehicleCategory>(bike?.category ?? 'motorcycle');
  const [make, setMake] = useState(bike?.make ?? '');
  const [seats, setSeats] = useState(bike?.seats ? String(bike.seats) : '');
  const [capacityKg, setCapacityKg] = useState(bike?.capacityKg ?? '');
  const [gpsImei, setGpsImei] = useState(bike?.gpsImei ?? '');
  const [status, setStatus] = useState<BikeStatus>(bike?.status ?? 'active');
  const [note, setNote] = useState(bike?.note ?? '');

  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const fields = error instanceof ApiError ? error.fields : undefined;

  return (
    <Modal open onClose={onClose} title={bike ? `Edit ${bike.registration}` : 'Add a bike'} size="lg">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          const body = {
            registration,
            category,
            ...(make ? { make } : {}),
            ...(model ? { model } : {}),
            ...(seats ? { seats: Number(seats) } : {}),
            ...(capacityKg ? { capacityKg } : {}),
            ...(purchaseDate ? { purchaseDate } : {}),
            ...(acquisitionCost ? { acquisitionCost } : {}),
            ...(estimatedValue ? { estimatedValue } : {}),
            ...(ownerName && ownership !== 'investor' ? { ownerName } : {}),
            branchId: branchId || null,
            ...(gpsImei ? { gpsImei } : {}),
            status,
            ...(note ? { note } : {}),
          };
          if (bike) {
            // The owner changes from the bike, with a date — never from here.
            update.mutate({ id: bike.id, input: body }, { onSuccess: onClose });
          } else {
            // A new bike may start in somebody's hands; the service opens its
            // first assignment row so the history begins where the bike does.
            create.mutate(
              {
                ...body,
                ownership: ownership as BikeOwnership,
                ...(ownership === 'investor' ? { investorId } : {}),
                ...(ownership === 'investor' && investorWeeklyAmount.trim() ? { investorWeeklyAmount: investorWeeklyAmount.trim() } : {}),
                riderId: riderId || null,
              },
              { onSuccess: onClose },
            );
          }
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Registration"
            error={fields?.registration?.[0]}
            required
            value={registration}
            onChange={(e) => setRegistration(e.target.value)}
            placeholder="CH 204 ABC"
            autoFocus
          />
          <Select
            label="Vehicle type"
            value={category}
            onChange={(e) => setCategory(e.target.value as VehicleCategory)}
            options={VEHICLE_CATEGORIES.map((value) => ({
              value,
              label: VEHICLE_CATEGORY_LABELS[value],
            }))}
            hint="What it is. Everything on the register before this was a motorcycle."
          />
          <Input
            label="Make"
            error={fields?.make?.[0]}
            value={make}
            onChange={(e) => setMake(e.target.value)}
            placeholder="Bajaj"
          />

          <Input
            label="Model"
            error={fields?.model?.[0]}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Bajaj Boxer"
          />

          <Input
            label="Purchased on"
            error={fields?.purchaseDate?.[0]}
            type="date"
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
          />

          <Input
            label="Acquisition cost"
            error={fields?.acquisitionCost?.[0]}
            hint="What it cost. Never overwritten with today's value."
            value={acquisitionCost}
            onChange={(e) => setAcquisitionCost(e.target.value)}
            inputMode="decimal"
          />

          <Input
            label="Estimated value"
            error={fields?.estimatedValue?.[0]}
            value={estimatedValue}
            onChange={(e) => setEstimatedValue(e.target.value)}
            inputMode="decimal"
          />

          <Select
            label="Owned by"
            error={fields?.ownership?.[0]}
            required
            disabled={Boolean(bike)}
            value={ownership}
            onChange={(e) => setOwnership(e.target.value as BikeOwnership | '')}
            placeholder="Choose who owns it…"
            options={
              bike && !SELECTABLE_BIKE_OWNERSHIPS.includes(bike.ownership as never)
                ? [...OWNERSHIP_OPTIONS, { value: bike.ownership, label: BIKE_OWNERSHIP_LABELS[bike.ownership] }]
                : OWNERSHIP_OPTIONS
            }
            hint={
              bike
                ? 'Change the owner from the bike: it takes the date it applies from.'
                : ownership === 'investor'
                  ? 'The investor is entitled to part of each week’s remittance.'
                  : ownership === 'rider'
                    ? 'A rider-owned bike pays no weekly remittance.'
                    : ownership === 'cenohub'
                      ? 'All of the weekly remittance is the company’s.'
                      : 'Company, Investor or Rider. Required.'
            }
          />

          {!bike && ownership === 'investor' ? (
            <Select
              label="Investor"
              error={fields?.investorId?.[0]}
              required
              value={investorId}
              onChange={(e) => setInvestorId(e.target.value)}
              placeholder={
                (investors.data ?? []).length === 0 ? 'Add the investor in Finance → Investors first' : 'Choose the investor…'
              }
              options={(investors.data ?? []).map((i) => ({
                value: i.id,
                label: i.reference ? `${i.name} (${i.reference})` : i.name,
              }))}
            />
          ) : null}

          {!bike && ownership === 'investor' && mayShare ? (
            <Input
              label="Investor’s share a week"
              inputMode="decimal"
              value={investorWeeklyAmount}
              onChange={(e) => setInvestorWeeklyAmount(e.target.value)}
              hint="Leave blank for the business default. Change it later from the bike’s ownership, from a date."
              error={fields?.investorWeeklyAmount?.[0]}
            />
          ) : null}

          {ownership === 'rider' || ownership === 'other' ? (
            <Input
              label="Owner's name"
              error={fields?.ownerName?.[0]}
              hint="Optional"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
            />
          ) : null}

          <Select
            label="Location"
            error={fields?.branchId?.[0]}
            hint="Blank is business-level"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            placeholder="Unallocated"
            options={locations.map((b) => ({ value: b.id, label: b.name }))}
          />

          {!bike ? (
            <Select
              label="Assigned to"
              error={fields?.riderId?.[0]}
              hint="Optional — this opens its first assignment"
              value={riderId}
              onChange={(e) => setRiderId(e.target.value)}
              placeholder="Nobody yet"
              options={(riders.data?.data ?? []).map((r) => ({
                value: r.id,
                label: r.canonicalName,
              }))}
            />
          ) : null}

          <Input
            label="GPS tracker (IMEI)"
            error={fields?.gpsImei?.[0]}
            value={gpsImei}
            onChange={(e) => setGpsImei(e.target.value)}
            inputMode="numeric"
          />

          <Select
            label="Status"
            error={fields?.status?.[0]}
            value={status}
            onChange={(e) => setStatus(e.target.value as BikeStatus)}
            options={STATUS_OPTIONS}
          />
        </div>

        <Textarea
          label="Notes"
          error={fields?.note?.[0]}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
        />

        {error && !fields ? <ErrorState message={error.message} /> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={pending || (!bike && (ownership === '' || (ownership === 'investor' && !investorId)))}
          >
            {bike ? 'Save changes' : 'Add to the register'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** One bike: its details, who has it, and everybody who ever did. */
function BikeDetailDialog({
  id,
  currency,
  mayManage,
  mayManageMoney,
  onClose,
}: {
  id: string;
  currency: string;
  mayManage: boolean;
  mayManageMoney: boolean;
  onClose: () => void;
}): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'Africa/Lagos');

  const detail = useBike(id);
  const riders = useRiders();
  const assign = useAssignBike();
  const release = useReleaseBike();

  const [editing, setEditing] = useState(false);
  const [riderId, setRiderId] = useState('');
  const [assignedOn, setAssignedOn] = useState(today);

  const error = assign.error ?? release.error;
  const fields = error instanceof ApiError ? error.fields : undefined;
  const bike = detail.data?.data;

  if (editing && bike) {
    return <BikeDialog bike={bike} onClose={() => setEditing(false)} />;
  }

  return (
    <Modal open onClose={onClose} title={bike?.registration ?? 'Bike'} size="lg">
      {!bike ? (
        <TableSkeleton rows={3} columns={2} />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Cost" value={bike.acquisitionCost} currency={currency} />
            <Stat label="Value now" value={bike.estimatedValue} currency={currency} />
            <div className="rounded-xl border border-slate-200 bg-white p-3.5">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Owner</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{ownerLabel(bike).kind}</p>
              <OwnerName bike={bike} className="mt-0.5 block text-xs text-slate-400" />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3.5">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Status</p>
              <div className="mt-1.5">
                <BikeStatusBadge status={bike.status} />
              </div>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-slate-500">Model</dt>
            <dd className="text-slate-900">{bike.model ?? '—'}</dd>
            <dt className="text-slate-500">Purchased</dt>
            <dd className="text-slate-900">{bike.purchaseDate ?? '—'}</dd>
            <dt className="text-slate-500">Location</dt>
            <dd className="text-slate-900">{bike.branchName ?? 'Unallocated'}</dd>
            <dt className="text-slate-500">GPS tracker</dt>
            <dd className="font-mono text-slate-900">{bike.gpsImei ?? '—'}</dd>
            <dt className="text-slate-500">Hire purchase</dt>
            <dd className="text-slate-900">
              {bike.agreementId ? 'Live agreement — see the Hire Purchase tab' : 'None'}
            </dd>
          </dl>

          {bike.note ? <p className="text-sm text-slate-600">{bike.note}</p> : null}

          {mayManage ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                {bike.riderId ? 'Reassign or take back' : 'Assign to a rider'}
              </p>
              <form
                className="flex flex-wrap items-start gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  assign.mutate({ id: bike.id, input: { riderId, assignedOn } });
                }}
              >
                <div className="min-w-[160px] flex-1">
                  <Select
                    label="Rider"
                    error={fields?.riderId?.[0]}
                    value={riderId}
                    onChange={(e) => setRiderId(e.target.value)}
                    placeholder="Choose a rider…"
                    options={(riders.data?.data ?? [])
                      .filter((r) => r.id !== bike.riderId)
                      .map((r) => ({ value: r.id, label: r.canonicalName }))}
                  />
                </div>
                <div>
                  <Input
                    label="From"
                    error={fields?.assignedOn?.[0]}
                    type="date"
                    value={assignedOn}
                    onChange={(e) => setAssignedOn(e.target.value)}
                  />
                </div>
                <div className="flex gap-2 self-end pb-0.5">
                  <Button type="submit" size="sm" disabled={assign.isPending || riderId === ''}>
                    Assign
                  </Button>
                  {bike.riderId ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={release.isPending}
                      onClick={() => release.mutate({ id: bike.id, releasedOn: today })}
                    >
                      Take back
                    </Button>
                  ) : null}
                </div>
              </form>
              {error && !fields ? (
                <div className="mt-2">
                  <ErrorState message={error.message} />
                </div>
              ) : null}
            </div>
          ) : null}

          <BikeOwnershipPanel
            bike={bike}
            currency={currency}
            mayChange={mayManage && mayManageMoney}
            mayChangeShare={mayManageMoney}
          />

          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
              Who has had it
            </p>
            {bike.assignments.length === 0 ? (
              <p className="text-sm text-slate-500">Never assigned.</p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {bike.assignments.map((a) => (
                  <li
                    key={a.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <span className="font-medium text-slate-900">{a.riderName}</span>
                    <span className="text-xs text-slate-500">
                      {a.assignedOn} – {a.releasedOn ?? 'now'}
                      {/* Who owned the bike while this rider had it. */}
                      {' · '}
                      {ownerAt(bike, a.assignedOn)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
            {mayManage ? (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Edit details
              </Button>
            ) : null}
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Who owned the bike on a date, from its ownership history. */
function ownerAt(
  bike: { ownershipHistory: Array<{ ownership: BikeOwnership; effectiveFrom: string; investorId: string | null; investorName: string | null }> },
  on: string,
): ReactNode {
  const sorted = [...bike.ownershipHistory].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  const period = [...sorted].reverse().find((p) => p.effectiveFrom <= on) ?? sorted[0];
  if (!period) return '—';
  return period.ownership === 'investor' && period.investorName ? (
    <>
      Investor: <InvestorName investorId={period.investorId} name={period.investorName} />
    </>
  ) : (
    `${BIKE_OWNERSHIP_LABELS[period.ownership]}-owned`
  );
}
